import { GfPromotionTag } from "@swagger/typescript-fetch-goflyer";

/** True when a change-request field key targets the promotion-tag relation. */
export const isPromotionTagField = (fieldKey?: unknown): boolean =>
  String(fieldKey) === "promotionTag";

/**
 * Coerce a promotion-tag change-request value into an array of elements.
 * Accepts a real array or a JSON-encoded array string; anything else → [].
 * Elements may be tag UUID strings or tag objects ({ id, name, ... }).
 */
export const toTagArray = (value: unknown): unknown[] => {
  let raw: unknown = value;
  if (typeof raw === "string") {
    try {
      raw = JSON.parse(raw) as unknown;
    } catch {
      return [];
    }
  }
  return Array.isArray(raw) ? raw : [];
};

/**
 * Resolve a promotion-tag CR value to display names. Each element may be a tag
 * UUID string (resolved via the admin promotion-tag list) or a tag object that
 * already carries its `name`. Unknown ids fall back to a short id snippet.
 */
export const resolvePromotionTagNames = (
  value: unknown,
  tags: GfPromotionTag[] | undefined,
): string[] => {
  const elements = toTagArray(value);
  if (elements.length === 0) return [];
  const byId = new Map<string, string>(
    (tags ?? []).map((tag) => [tag.id, tag.name]),
  );
  return elements
    .map((el) => {
      if (typeof el === "string") {
        return byId.get(el) ?? el.slice(0, 8);
      }
      if (el && typeof el === "object") {
        const obj = el as { id?: unknown; name?: unknown };
        if (typeof obj.name === "string" && obj.name.length > 0) {
          return obj.name;
        }
        if (typeof obj.id === "string") {
          return byId.get(obj.id) ?? obj.id.slice(0, 8);
        }
      }
      return "";
    })
    .filter((name) => name.length > 0);
};
